import { TiktokAudioAsset, getTiktokAudioAssets, getTiktokAudios } from '@round/api';
import { useCallback, useMemo, useState } from 'react';
import { showNotification } from '../../../../../helpers';
import { TiktokAudio } from '@round/api';
import { AudioOption } from './AudioSelectComponents/AudioSelectComponents';
import { ValueType } from 'react-select';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';
import debounce from 'lodash/debounce';

const PAGE_SIZE = 10;

const makeAudioOption = (audio: TiktokAudio, asset?: TiktokAudioAsset | undefined): AudioOption => ({
    value: audio.id,
    title: audio.title,
    authorName: audio.author_name,
    tiktokId: audio.tiktok_id,
    isOriginal: audio.is_original,
    imageUrl: asset?.image_thumb.cached_url || asset?.image_thumb.original_url || '',
    audioPlayUrl: asset?.audio_file.cached_url || asset?.audio_file.original_url || '',
});

export const fetchInitialSelectedOptions = async (audioIds: number[], requestInit?: RequestInit) => {
    try {
        const [audiosResult, assetsResult] = await Promise.allSettled([
            getTiktokAudios({ id: audioIds.join(), page_size: audioIds.length }, requestInit),
            getTiktokAudioAssets(audioIds, requestInit),
        ]);

        const audios = audiosResult.status === 'fulfilled' ? audiosResult.value.data.results : [];
        const assets = assetsResult.status === 'fulfilled' ? assetsResult.value : [];

        return audios.map<AudioOption>((audio) => {
            const asset = assets.find((asset) => asset.audio_id === audio.id);
            return makeAudioOption(audio, asset);
        });
    } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') {
            return;
        }

        showNotification((e as Error).message, 'error');
    }
};

export function useAudioSelect() {
    const [selectedAudioOptions, setSelectedAudioOptions] = useState<ValueType<AudioOption, true>>([]);

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const [audios, setAudios] = useState<TiktokAudio[]>([]);
    const [assets, setAssets] = useState<TiktokAudioAsset[]>([]);

    const fetchData = useCallback(async (params: Parameters<typeof getTiktokAudios>[0], requestInit?: RequestInit) => {
        try {
            setIsLoading(true);
            const {
                data: { results, next },
            } = await getTiktokAudios(params, requestInit);

            setAudios((prev) => prev.concat(results));
            setHasNextPage(!!next);

            const audioIds = results.map((audio) => audio.id);

            if (!audioIds.length) {
                return;
            }

            const assets = await getTiktokAudioAssets(audioIds, requestInit);
            setAssets((prev) => prev.concat(assets));
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }
            showNotification((e as Error).message, 'error');
        } finally {
            setIsLoading(false);
            setIsSearchLoading(false);
        }
    }, []);

    const debouncedFetchData = useMemo(() => debounce(fetchData, 700), [fetchData]);

    useAbortableEffect(
        (signal) => {
            if (!search) {
                return;
            }
            debouncedFetchData({ tiktok_id: search, page, page_size: PAGE_SIZE }, { signal });
        },
        [debouncedFetchData, page, search]
    );

    const reset = useCallback(() => {
        setAudios([]);
        setAssets([]);
        setPage(1);
        setHasNextPage(false);
    }, []);

    const options: AudioOption[] = useMemo(
        () =>
            audios.map((item) => {
                const asset = assets.find((asset) => asset.audio_id === item.id);
                return makeAudioOption(item, asset);
            }),
        [assets, audios]
    );

    const initSelectedOptions = useCallback(
        async (audioOptions: AudioOption[]) => {
            setSelectedAudioOptions(audioOptions);
            setIsInitialized(true);
        },
        [setSelectedAudioOptions]
    );

    return {
        selectProps: {
            options,
            value: selectedAudioOptions,
            onChange: (value: ValueType<AudioOption, true>) => {
                setSelectedAudioOptions(value);
            },
            inputValue: search,
            onInputChange: (value: string) => {
                if (value === search) {
                    return;
                }

                reset();
                setSearch(value);

                if (value) {
                    setIsSearchLoading(true);
                }
            },
            onMenuScrollToBottom: () => {
                if (!hasNextPage) {
                    return;
                }
                setPage((prev) => prev + 1);
            },
            noOptionsMessage: () => (!search ? 'Type to search for audios' : 'No audios found'),
            isLoading: isLoading || isSearchLoading,
        },
        initSelectedOptions,
        isInitialized,
    };
}
